/**
 * Everyone, across every organisation. Super admin only.
 *
 * The two things this can do that the per-organisation list cannot are moving
 * somebody to a different organisation and narrowing what they may see. Both
 * are quiet in their effects and loud in their consequences, so both confirm
 * what is about to happen in words rather than ids.
 */
import { useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Card, Group, Loader, Modal, MultiSelect, Select, Stack, Table, Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as gw from '../../api/gateway';
import { useCampaigns, useEntities } from '../../api/queries';
import { formatDateTime } from '../../theme';
import { CustomiseUser } from './CustomiseUser';
import { roleLabel, useVocab } from '../../vocab';

export function UsersTab() {
  const v = useVocab();
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ['all-users'], queryFn: gw.allUsers });
  const orgs = useQuery({ queryKey: ['tenants'], queryFn: gw.tenants });
  const [search, setSearch] = useState('');
  const [moving, setMoving] = useState<gw.GatewayAccount | null>(null);
  const [scoping, setScoping] = useState<gw.GatewayAccount | null>(null);
  const [removing, setRemoving] = useState<gw.GatewayAccount | null>(null);
  const [customising, setCustomising] = useState<gw.GatewayAccount | null>(null);

  const shown = useMemo(() => {
    const all = users.data?.users ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        `${u.first_name ?? ''} ${u.last_name ?? ''}`.toLowerCase().includes(q) ||
        (u.tenant_name ?? '').toLowerCase().includes(q),
    );
  }, [users.data, search]);

  const orgName = (id?: string | null) =>
    orgs.data?.tenants.find((t) => t.id === id)?.name ?? id ?? '-';

  return (
    <Stack>
      <Group justify="space-between">
        <Text fw={600}>Everyone</Text>
        <TextInput
          placeholder="Search by name, email or organisation"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          w={320}
        />
      </Group>

      <Card withBorder radius="md" padding={0}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Organisation</Table.Th>
              <Table.Th>Role</Table.Th>
              <Table.Th>Last signed in</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {users.isPending && (
              <Table.Tr><Table.Td colSpan={6}>
                <Group justify="center" py="lg"><Loader size="sm" /></Group>
              </Table.Td></Table.Tr>
            )}
            {!users.isPending && shown.length === 0 && (
              <Table.Tr><Table.Td colSpan={6}>
                <Text ta="center" c="dimmed" py="lg">
                  {search.trim() ? 'Nobody matches that search.' : 'Nobody yet.'}
                </Text>
              </Table.Td></Table.Tr>
            )}
            {shown.map((u) => (
              <Table.Tr key={u.id}>
                <Table.Td>
                  <Text size="sm">{[u.first_name, u.last_name].filter(Boolean).join(' ') || '-'}</Text>
                </Table.Td>
                <Table.Td><Text size="sm">{u.email}</Text></Table.Td>
                <Table.Td><Text size="sm">{u.tenant_name ?? orgName(u.tenant_id)}</Text></Table.Td>
                <Table.Td>
                  <Badge variant="light" color="gray">{roleLabel(v, u.role)}</Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {u.last_login_at ? formatDateTime(u.last_login_at) : 'Never'}
                  </Text>
                </Table.Td>
                <Table.Td w={280}>
                  <Group gap={4} wrap="nowrap">
                    <Button size="compact-xs" variant="subtle" onClick={() => setScoping(u)}>
                      Data
                    </Button>
                    <Button size="compact-xs" variant="subtle" onClick={() => setCustomising(u)}>
                      Permissions
                    </Button>
                    <Button size="compact-xs" variant="subtle" onClick={() => setMoving(u)}>
                      Move
                    </Button>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="red"
                      onClick={() => setRemoving(u)}
                    >
                      Delete
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>

      <MoveUser
        user={moving}
        orgs={orgs.data?.tenants ?? []}
        onClose={() => setMoving(null)}
        onDone={() => qc.invalidateQueries({ queryKey: ['all-users'] })}
      />
      <ScopeUser user={scoping} onClose={() => setScoping(null)} />
      <CustomiseUser user={customising} onClose={() => setCustomising(null)} />
      <DeleteUser
        user={removing}
        onClose={() => setRemoving(null)}
        onDone={() => qc.invalidateQueries({ queryKey: ['all-users'] })}
      />
    </Stack>
  );
}

function MoveUser({
  user,
  orgs,
  onClose,
  onDone,
}: {
  user: gw.GatewayAccount | null;
  orgs: gw.Tenant[];
  onClose: () => void;
  onDone: () => void;
}) {
  const v = useVocab();
  const [target, setTarget] = useState<string | null>(null);
  const move = useMutation({
    mutationFn: () => gw.moveUser(user!.id, target!),
    onSuccess: () => {
      onDone();
      onClose();
      setTarget(null);
      notifications.show({ color: 'teal', title: 'Moved', message: 'They belong to the new organisation now.' });
    },
    onError: (e) =>
      notifications.show({ color: 'red', title: 'Could not move them', message: (e as Error).message }),
  });

  return (
    <Modal opened={!!user} onClose={onClose} title="Move to another organisation" size="md">
      <Stack>
        <Alert color="chartAmber" variant="light">
          <Text size="sm">
            {user?.email} {`will immediately see the new organisation's ${v.many('lead')},
            ${v.many('campaign')} and files instead of their own, and lose sight of everything
            they can see now. Any ${v.one('campaign')} or ${v.one('buyer')} limits set for them
            are cleared, because those name things that do not exist in the new organisation.`}
          </Text>
        </Alert>
        <Select
          label="Move them to"
          data={orgs.filter((t) => t.id !== user?.tenant_id).map((t) => ({ value: t.id, label: t.name }))}
          value={target}
          onChange={setTarget}
          searchable
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button color="red" loading={move.isPending} disabled={!target} onClick={() => move.mutate()}>
            Move them
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function DeleteUser({
  user,
  onClose,
  onDone,
}: {
  user: gw.GatewayAccount | null;
  onClose: () => void;
  onDone: () => void;
}) {
  /* Typing the address is the confirmation. A dialog whose only defence is a
     red button gets clicked through, and this cannot be undone. */
  const [typed, setTyped] = useState('');
  const remove = useMutation({
    mutationFn: () => gw.deleteUser(user!.id),
    onSuccess: () => {
      onDone();
      onClose();
      setTyped('');
      notifications.show({ color: 'teal', title: 'Deleted', message: 'The account is gone.' });
    },
    onError: (e) =>
      notifications.show({ color: 'red', title: 'Could not delete', message: (e as Error).message }),
  });

  return (
    <Modal opened={!!user} onClose={onClose} title="Delete this account" size="md">
      <Stack>
        <Alert color="red" variant="light">
          <Text size="sm">
            {user?.email} loses access immediately and permanently. Anything they created stays
            where it is, but the account cannot be brought back, only made again.
          </Text>
        </Alert>
        <TextInput
          label={`Type ${user?.email ?? ''} to confirm`}
          value={typed}
          onChange={(e) => setTyped(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={() => { setTyped(''); onClose(); }}>Cancel</Button>
          <Button
            color="red"
            loading={remove.isPending}
            disabled={typed.trim() !== user?.email}
            onClick={() => remove.mutate()}
          >
            Delete the account
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function ScopeUser({ user, onClose }: { user: gw.GatewayAccount | null; onClose: () => void }) {
  const v = useVocab();
  const campaigns = useCampaigns();
  const buyers = useEntities('buyers');
  const suppliers = useEntities('suppliers');
  const scope = useQuery({
    queryKey: ['user-scope', user?.id],
    queryFn: async () => ({
      campaigns: (await gw.userCampaigns(user!.id)).campaign_ids,
      buyers: (await gw.userBuyers(user!.id)).buyer_ids,
      suppliers: (await gw.userSuppliers(user!.id)).supplier_ids,
    }),
    enabled: !!user,
  });

  const [camps, setCamps] = useState<string[] | null>(null);
  const [buys, setBuys] = useState<string[] | null>(null);
  const [sups, setSups] = useState<string[] | null>(null);
  /* Nothing on this form may be shown or saved until every query has landed.
     An empty list here means "everything", so the moment before the answer
     arrives looks exactly like an unrestricted user, and saving from that
     state would silently hand somebody the whole account. The option lists
     matter too: without them Mantine has no label for a chosen id and shows
     the raw uuid as a pill. */
  const loading =
    scope.isPending || campaigns.isPending || buyers.isPending || suppliers.isPending;
  const chosenCamps = camps ?? scope.data?.campaigns ?? [];
  const chosenBuys = buys ?? scope.data?.buyers ?? [];
  const chosenSups = sups ?? scope.data?.suppliers ?? [];

  /* The modal stays mounted between openings, so anything typed for one
     person would still be on screen for the next one. Clearing on the way out
     is what stops user B being shown user A's unsaved restrictions. */
  const close = () => {
    setCamps(null);
    setBuys(null);
    setSups(null);
    onClose();
  };

  const save = useMutation({
    mutationFn: async () => {
      // An empty list means "everything", not "nothing": the restriction is a
      // set of rows, and someone with no rows is unrestricted. Sending the
      // empty array is what clears it. This used to send null, which the
      // gateway rejects with "campaign_ids array is required", so putting
      // somebody back to seeing everything failed every time.
      await gw.setUserCampaigns(user!.id, chosenCamps);
      await gw.setUserBuyers(user!.id, chosenBuys);
      await gw.setUserSuppliers(user!.id, chosenSups);
    },
    onSuccess: () => {
      close();
      notifications.show({ color: 'teal', title: 'Saved', message: 'What they can see has been updated.' });
    },
    onError: (e) =>
      notifications.show({ color: 'red', title: 'Could not save', message: (e as Error).message }),
  });

  return (
    <Modal opened={!!user} onClose={close} title={`What ${user?.email ?? ''} can see`} size="lg">
      {loading ? (
        <Group justify="center" py="xl"><Loader size="sm" /></Group>
      ) : (
      <Stack>
        <Text size="sm" c="dimmed">
          Leave a list empty to let them see everything of that kind. Choosing any narrows them
          to exactly those.
        </Text>
        <MultiSelect
          label={v.Many('campaign')}
          placeholder={chosenCamps.length ? undefined : `All ${v.many('campaign')}`}
          data={(campaigns.data?.campaigns ?? []).map((c) => ({ value: c.id, label: c.name }))}
          value={chosenCamps}
          onChange={setCamps}
          searchable
          clearable
        />
        <MultiSelect
          label={v.Many('buyer')}
          placeholder={chosenBuys.length ? undefined : `All ${v.many('buyer')}`}
          data={(buyers.data?.entities ?? []).map((b) => ({ value: b.id, label: b.name }))}
          value={chosenBuys}
          onChange={setBuys}
          searchable
          clearable
        />
        <MultiSelect
          label={v.Many('supplier')}
          placeholder={chosenSups.length ? undefined : `All ${v.many('supplier')}`}
          data={(suppliers.data?.entities ?? []).map((sp) => ({ value: sp.id, label: sp.name }))}
          value={chosenSups}
          onChange={setSups}
          searchable
          clearable
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={close}>Cancel</Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
        </Group>
      </Stack>
      )}
    </Modal>
  );
}
